import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useReactTable, getCoreRowModel, getExpandedRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState, ColumnOrderState, ColumnSizingState } from '@tanstack/react-table';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
import Loader from '../../common/Loader';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { getValueFromResource, normalizeDateFormat } from '../../common/timeLiveFunction';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { TOOLTIP_STYLE } from '../../common/TooltipStyle';
import infoSVG from "../../assets/image/info-SVG.svg";

const ProgressBar: React.FC<{ value: number }> = ({ value }) => {
    const pct = Math.max(0, Math.min(100, value));
    let color = '#639922';
    if (value < 90) color = '#E24B4A';
    else if (value > 110) color = '#EF9F27';

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 90 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 20, background: '#E9ECEF', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 20, background: color, transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: 11, color: '#495057', fontWeight: 500, minWidth: 38, textAlign: 'right', flexShrink: 0 }}>
                {value}%
            </span>
        </div>
    );
};

const UtilizationBadge: React.FC<{ value: number }> = ({ value }) => {
    let label = 'Optimal', bg = '#EAF3DE', color = '#3B6D11', dot = '#639922';
    if (value < 90) {
        label = 'Underutilized'; bg = '#FCEBEB'; color = '#A32D2D'; dot = '#E24B4A';
    } else if (value > 110) {
        label = 'Overutilized'; bg = '#FAEEDA'; color = '#854F0B'; dot = '#EF9F27';
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />
            {label}
        </span>
    );
};

const ROW_TYPE_ICON_MAP: Record<string, { icon: string; color: string; bg: string }> = {
    project: { icon: 'bi-folder2-open', color: '#4361EE', bg: '#fff' },
    task: { icon: 'bi-list-task', color: '#4361EE', bg: '#E6F1FB' },
    user: { icon: 'bi-person-fill', color: '#4361EE', bg: '#E6F1FB' },
    client: { icon: 'bi-person-fill', color: '#4361EE', bg: '#E6F1FB' },
    month: { icon: 'bi-calendar3', color: '#4361EE', bg: '#E6F1FB' },
    week: { icon: 'bi-calendar3', color: '#4361EE', bg: '#E6F1FB' },
    date: { icon: 'bi-calendar3', color: '#4361EE', bg: '#E6F1FB' },
    day: { icon: 'bi-calendar3', color: '#4361EE', bg: '#E6F1FB' },
};

const DEFAULT_ROW_TYPE_ICON = { icon: 'bi-list-task', color: '#4361EE', bg: '#E6F1FB' };
const RowTypeIcon: React.FC<{ groupType?: string }> = ({ groupType }) => {
    const key = (groupType || '').toString().trim().toLowerCase();
    const c = ROW_TYPE_ICON_MAP[key] || DEFAULT_ROW_TYPE_ICON;
    return (
        <span
            style={{
                width: 22, height: 22, borderRadius: 6,
                background: 'transparent', color: c.color,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, flexShrink: 0,
            }}
        >
            <i className={`bi ${c.icon}`} />
        </span>
    );
};


const ExpandToggle: React.FC<{ canExpand: boolean; isExpanded: boolean; onClick: () => void }> = ({ canExpand, isExpanded, onClick }) => {
    if (!canExpand) {
        return <span style={{ width: 20, height: 20, flexShrink: 0, marginRight: 8 }} />;
    }
    return (
        <button
            onClick={onClick}
            style={{
                width: 20, height: 20, borderRadius: 5,
                border: '1px solid #E9ECEF', background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, marginRight: 8,
                transition: 'transform 0.15s ease, background 0.15s ease, border-color 0.15s ease',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F1F3FF'; e.currentTarget.style.borderColor = '#C7D2FE'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E9ECEF'; }}
        >
            <i className="bi bi-chevron-right" style={{ fontSize: 9, color: '#6B7280' }} />
        </button>
    );
};

const formatHoursToHHMMSS = (decimalHours: number) => {
    const isNegative = decimalHours < 0;
    const totalSeconds = Math.round(Math.abs(decimalHours || 0) * 3600);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${isNegative ? '-' : ''}${pad(hrs)}:${pad(mins)}:${pad(secs)}h`;
};

const ScheduleVSTrackingHoursList = (props: any) => {
    const { data, loading } = props;
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([
        'name', 'scheduledHours', 'trackingHours', 'differenceHours', 'utilization', 'statusPercentage'
    ]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef(0);

    const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
        scrollPositionRef.current = e.currentTarget.scrollTop;
    };

    useLayoutEffect(() => {
        if (!loading && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [loading, data]);

    const columns = useMemo<ColumnDef<any>[]>(() => [
        {
            id: 'name',
            accessorFn: (row) => row.GroupLabel,
            header: () => getValueFromResource('Name', dispatch, t),
            cell: ({ row, getValue }) => (
                <div style={{ position: 'relative', paddingLeft: row.depth * 25, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {Array.from({ length: row.depth }).map((_, i) => (
                        <span
                            key={i}
                            style={{
                                position: 'absolute',
                                left: i * 25 + 10,
                                top: -10,
                                bottom: -10,
                                width: 1,
                                background: '#E9ECEF',
                            }}
                        />
                    ))}

                    <ExpandToggle
                        canExpand={row.getCanExpand()}
                        isExpanded={row.getIsExpanded()}
                        onClick={row.getToggleExpandedHandler()}
                    />

                    <span style={{ marginRight: 8, flexShrink: 0 }}>
                        <RowTypeIcon groupType={row.original?.GroupType} />
                    </span>

                    <span
                        className={row.depth === 0 ? 'fw-bold text-dark' : row.depth === 1 ? 'text-gray-700' : 'text-gray-500'}
                        style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {getValue() as string}
                    </span>
                </div>
            ),
            enableSorting: true,
            size: 280, minSize: 180, maxSize: 500,
        },
        {
            id: 'scheduledHours',
            accessorKey: 'ScheduledHours',
            header: () => (
                <span
                    data-tooltip-id="tooltip-description"
                    data-tooltip-content="Hours assigned through Resource Scheduler"
                    style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                    {getValueFromResource('Scheduled', dispatch, t)}
                </span>
            ),
            cell: ({ getValue }) => {
                const v = getValue();
                return <span style={{ fontSize: 12, color: '#212529' }}>{v != null ? formatHoursToHHMMSS(Number(v)) : '—'}</span>;
            },
            //cell: ({ getValue }) => <span style={{ fontSize: 12, color: '#212529' }}>{getValue() != null ? String(getValue()) : '—'}</span>,
            enableSorting: true,
            size: 120, minSize: 90, maxSize: 200,
        },
        {
            id: 'trackingHours',
            accessorKey: 'TrackingHours',
            header: () => (
                <span
                    data-tooltip-id="tooltip-description"
                    data-tooltip-content="Hours recorded through Timesheets"
                    style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                    {getValueFromResource('Tracked', dispatch, t)}
                </span>
            ),
            cell: ({ getValue }) => {
                const v = getValue();
                return <span style={{ fontSize: 12, color: '#212529' }}>{v != null ? formatHoursToHHMMSS(Number(v)) : '—'}</span>;
            },
            //cell: ({ getValue }) => <span style={{ fontSize: 12, color: '#212529' }}>{getValue() != null ? String(getValue()) : '—'}</span>,
            enableSorting: true,
            size: 120, minSize: 90, maxSize: 200,
        },
        {
            id: 'differenceHours',
            accessorKey: 'DifferenceHours',
            header: () => getValueFromResource('Difference', dispatch, t),
            cell: ({ getValue }) => {
                const v = getValue();
                return <span style={{ fontSize: 12, color: '#212529' }}>{v != null ? formatHoursToHHMMSS(Number(v)) : '—'}</span>;
            },
            // cell: ({ getValue }) => <span style={{ fontSize: 12, color: '#212529' }}>{getValue() != null ? String(getValue()) : '—'}</span>,
            enableSorting: true,
            size: 100, minSize: 80, maxSize: 180,
        },
        {
            id: 'utilization',
            accessorKey: 'StatusPercentage',
            header: () => (
                <span
                    data-tooltip-id="tooltip-description"
                    data-tooltip-content="Utilization shows how tracked hours from timesheets compare to scheduled hours.
                                            Calculated as Tracked Hours ÷ Scheduled Hours × 100.
                                            Below 90%: Underutilized · 90–110%: Optimal · Above 110%: Overutilized."
                    style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                    {getValueFromResource('Utilization', dispatch, t)}
                </span>
            ),
            cell: ({ row, getValue }) => {
                const scheduledHours = Number(row.original.ScheduledHours);
                const raw = getValue();
                const val = raw != null ? Number(raw) : null;
                if (scheduledHours === 0) {
                    return <span style={{ fontSize: 12, color: '#212529' }}>∞</span>;
                }
                return val != null && !isNaN(val) ? <UtilizationBadge value={val} /> : <span>∞</span>;
            },
            enableSorting: true,
            size: 150,
            minSize: 130,
            maxSize: 220,
        },
        {
            id: 'statusPercentage',
            accessorKey: 'StatusPercentage',
            header: () => getValueFromResource('Progress %', dispatch, t),
            cell: ({ row, getValue }) => {
                const scheduledHours = Number(row.original.ScheduledHours);
                const differenceHours = Number(row?.original?.DifferenceHours);
                const raw = getValue();
                const val = raw != null ? Number(raw) : null;
                if (scheduledHours === 0) {
                    return <span style={{ fontSize: 12, color: '#212529' }}>∞</span>;
                }
                if (differenceHours < 0) {
                    return <span>—</span>;
                }
                return val != null && !isNaN(val) ? <ProgressBar value={val} /> : <span>—</span>;
            },
            enableSorting: true,
            size: 150, minSize: 130, maxSize: 220,
        },
    ], [dispatch, t]);

    const table = useReactTable({
        data,
        columns,
        state: { sorting, columnOrder, columnSizing },
        onSortingChange: setSorting,
        onColumnOrderChange: setColumnOrder,
        onColumnSizingChange: setColumnSizing,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        // getSubRows: (row: any) => row.SubRowsList || row.UserSubRows,
        getSubRows: (row: any) => row.SubRowsList,
        columnResizeMode: 'onChange',
        defaultColumn: {
            size: 150,
            minSize: 80,
            maxSize: 500,
        },
    });

    const totalSize = table.getTotalSize();
    const headerGroup = table.getHeaderGroups()[0];

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;
        if (sourceIndex === 0 || destIndex === 0) return;
        const newOrder = [...columnOrder];
        const [removed] = newOrder.splice(sourceIndex, 1);
        newOrder.splice(destIndex, 0, removed);
        setColumnOrder(newOrder);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {loading && <Loader />}
            <div
                ref={scrollContainerRef}
                onScroll={handleTableScroll}
                className='mt-2'
                style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 305px)', border: '1px solid #E9ECEF', borderRadius: 8, width: '100%', position: 'relative',isolation: 'isolate' }}
            >
                <table style={{ width: '100%', minWidth: totalSize, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                    <colgroup>
                        {headerGroup.headers.map(header => (
                            <col key={header.id} style={{ width: header.getSize() }} />
                        ))}
                    </colgroup>
                    <thead style={{ background: '#F8F9FA' }}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="scheduleColumns" direction="horizontal">
                                {provided => (
                                    <tr ref={provided.innerRef} {...provided.droppableProps}>
                                        {headerGroup.headers.map((header, index) => {
                                            const isLocked = header.column.id === 'name';
                                            return (
                                                <Draggable key={header.id} draggableId={header.id} index={index} isDragDisabled={isLocked}>
                                                    {(prov, snapshot) => {
                                                        const thStyle: React.CSSProperties = {
                                                            ...prov.draggableProps.style,
                                                            position: snapshot.isDragging ? (prov.draggableProps.style as any)?.position : 'sticky',
                                                            top: snapshot.isDragging ? (prov.draggableProps.style as any)?.top : 0,
                                                            padding: '10px 12px',
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: '#343A40',
                                                            borderBottom: '1px solid #E9ECEF',
                                                            borderRight: '1px solid #E9ECEF',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            background: snapshot.isDragging ? '#EEF0FD' : '#F8F9FA',
                                                            opacity: snapshot.isDragging ? 0.9 : 1,
                                                            boxShadow: snapshot.isDragging ? '0 4px 12px rgba(67,97,238,0.25)' : 'none',
                                                            // zIndex: snapshot.isDragging ? 9999 : undefined,
                                                            zIndex: snapshot.isDragging ? 3 : 1,
                                                            width: header.getSize(),
                                                            boxSizing: 'border-box',
                                                            display: snapshot.isDragging ? 'inline-block' : undefined,
                                                        };

                                                        // const thElement = (
                                                        //     <th
                                                        //         ref={prov.innerRef}
                                                        //         {...prov.draggableProps}
                                                        //         style={thStyle}
                                                        //     >
                                                        //         <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                                        //             {!isLocked && (
                                                        //                 <span
                                                        //                     {...prov.dragHandleProps}
                                                        //                     style={{ cursor: 'grab', color: '#C4C4C4', display: 'flex', alignItems: 'center', marginRight: 2, flexShrink: 0 }}
                                                        //                     title="Drag to reorder"
                                                        //                 >
                                                        //                     <i className="fa fa-grip-vertical six-dots-icon" style={{ fontSize: 12, color: '#6B7280', marginRight: 3, opacity: 0.7 }}></i>
                                                        //                 </span>
                                                        //             )}

                                                        //             <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                                        //                 {flexRender(header.column.columnDef.header, header.getContext())}
                                                        //             </span>

                                                        //             {header.column.getCanSort() && (
                                                        //                 <span
                                                        //                     onClick={header.column.getToggleSortingHandler()}
                                                        //                     style={{ display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer', marginLeft: 2, flexShrink: 0 }}
                                                        //                 >
                                                        //                     <span style={{ width: 0, height: 0, borderLeft: '3.5px solid transparent', borderRight: '3.5px solid transparent', borderBottom: `4px solid ${header.column.getIsSorted() === 'asc' ? '#4361EE' : '#C4C4C4'}` }} />
                                                        //                     <span style={{ width: 0, height: 0, borderLeft: '3.5px solid transparent', borderRight: '3.5px solid transparent', borderTop: `4px solid ${header.column.getIsSorted() === 'desc' ? '#4361EE' : '#C4C4C4'}` }} />
                                                        //                 </span>
                                                        //             )}
                                                        //             {header.column.getCanResize() && (
                                                        //                 <div
                                                        //                     onMouseDown={header.getResizeHandler()}
                                                        //                     onTouchStart={header.getResizeHandler()}
                                                        //                     style={{
                                                        //                         position: 'absolute',
                                                        //                         right: 0,
                                                        //                         top: 0,
                                                        //                         height: '100%',
                                                        //                         width: 5,
                                                        //                         cursor: 'col-resize',
                                                        //                         userSelect: 'none',
                                                        //                         touchAction: 'none',
                                                        //                         background: header.column.getIsResizing() ? '#4361EE' : 'transparent',
                                                        //                     }}
                                                        //                 />
                                                        //             )}
                                                        //         </div>
                                                        //     </th>
                                                        // );
                                                        const thElement = (
                                                            <th
                                                                ref={prov.innerRef}
                                                                {...prov.draggableProps}
                                                                style={thStyle}
                                                            >
                                                                <div
                                                                    {...(!isLocked ? prov.dragHandleProps : {})}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        width: '100%',
                                                                        minWidth: 0,
                                                                        cursor: snapshot.isDragging ? 'grabbing' : (isLocked ? 'default' : 'grab'),
                                                                    }}
                                                                >
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                                    </span>

                                                                    {header.column.getCanSort() && (
                                                                        <span
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                header.column.getToggleSortingHandler()?.(e);
                                                                            }}
                                                                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginLeft: 6, flexShrink: 0 }}
                                                                        >
                                                                            <i
                                                                                className={`bi ${header.column.getIsSorted() === 'asc' ? 'bi-sort-up' : header.column.getIsSorted() === 'desc' ? 'bi-sort-down' : 'bi-arrow-down-up'}`}
                                                                                style={{ fontSize: 13, color: header.column.getIsSorted() ? '#4361EE' : '#C4C4C4' }}
                                                                            />
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {header.column.getCanResize() && (
                                                                    <div
                                                                        onMouseDown={(e) => {
                                                                            e.stopPropagation();
                                                                            header.getResizeHandler()(e);
                                                                        }}
                                                                        onTouchStart={(e) => {
                                                                            e.stopPropagation();
                                                                            header.getResizeHandler()(e);
                                                                        }}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            right: 0,
                                                                            top: 0,
                                                                            height: '100%',
                                                                            width: 5,
                                                                            cursor: 'col-resize',
                                                                            userSelect: 'none',
                                                                            touchAction: 'none',
                                                                            background: header.column.getIsResizing() ? '#4361EE' : 'transparent',
                                                                        }}
                                                                    />
                                                                )}
                                                            </th>
                                                        );
                                                        return snapshot.isDragging ? ReactDOM.createPortal(thElement, document.body) : thElement;
                                                    }}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </tr>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr
                                key={row.id}
                                style={{ background: '#fff' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#F5F7FF')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        style={{ padding: '10px 12px', borderRight: '1px solid #E9ECEF', borderBottom: '1px solid #E9ECEF', verticalAlign: 'middle', width: cell.column.getSize(), overflow: 'hidden', position: 'relative', zIndex: 0 }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {!loading && table.getRowModel().rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="text-center text-muted py-4" style={{ padding: '10px 12px', verticalAlign: 'middle', position: 'relative', zIndex: 0 }}>
                                    <p className="noDataFound">
                                        {getValueFromResource('No Record Found', dispatch, t)}
                                    </p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <ReactTooltip
                id="tooltip-description"
                style={TOOLTIP_STYLE}
                place="bottom"
                delayShow={100}
                delayHide={100}
                clickable
                render={({ content }) => {
                    const isLongContent = content && content?.length > 90;
                    return (
                        <div style={{ maxWidth: isLongContent ? '380px' : '220px' }}>
                            {content}
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default ScheduleVSTrackingHoursList;